/** @jsx jsx */
import { jsx } from "theme-ui";
import Divider from "../../elements/divider";
import { Float, Drift, Twinkle } from "../../styles/animations";
import Planet from "./Planet";
import { rotatePlanet } from "./animations";

interface PlanetsProps {
  offset: number;
  factor?: number;
}

const Planets = ({ offset, factor = 1.5 }: PlanetsProps) => (
  <Divider speed={0.1} offset={offset} factor={factor} zIndex={10}>
    {/* Earth Planet - no ring since Earth doesn't have rings */}
    <div
      sx={{
        position: "absolute",
        left: ["5%", "8%"],
        top: ["15%", "18%"],
        "@media screen and (max-width: 600px)": {
          left: "3%",
          top: "12%",
        },
      }}
    >
      <Drift>
        <div
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: ["100px", "140px"],
            height: ["100px", "140px"],
            borderRadius: "50%",
            overflow: "hidden",
            animation: `${rotatePlanet} 120s linear infinite`,
            willChange: "transform",
            backfaceVisibility: "hidden",
            transition: "transform 0.3s ease-out",
            filter: "drop-shadow(0 0 15px rgba(70, 130, 180, 0.4)) drop-shadow(0 0 25px rgba(135, 206, 250, 0.3))",
            "&:hover": {
              transform: "translate(-50%, -50%) scale(1.05)",
              zIndex: 20,
            },
          }}
        >
      <div
        sx={{
          width: "100%",
          height: "100%",
          background: `
            radial-gradient(ellipse 100% 60% at 50% 50%,
              rgba(30, 144, 255, 0.9) 0%,
              rgba(25, 25, 112, 0.8) 100%
            ),
            radial-gradient(ellipse 25% 30% at 35% 40%,
              rgba(34, 139, 34, 0.7) 0%,
              rgba(46, 125, 50, 0.6) 50%,
              transparent 100%
            ),
            radial-gradient(ellipse 20% 25% at 65% 60%,
              rgba(139, 69, 19, 0.6) 0%,
              rgba(101, 67, 33, 0.5) 50%,
              transparent 100%
            ),
            radial-gradient(ellipse 18% 22% at 20% 70%,
              rgba(34, 139, 34, 0.6) 0%,
              rgba(46, 125, 50, 0.5) 50%,
              transparent 100%
            ),
            radial-gradient(ellipse 15% 20% at 75% 25%,
              rgba(139, 69, 19, 0.5) 0%,
              rgba(101, 67, 33, 0.4) 50%,
              transparent 100%
            ),
            /* Realistic cloud layers - wispy, organic patterns like Earth from space */
            /* Large swirling cloud systems - major weather patterns */
            radial-gradient(ellipse 45% 35% at 50% 50%,
              transparent 0%,
              rgba(255, 255, 255, 0.3) 20%,
              rgba(250, 250, 255, 0.4) 35%,
              rgba(240, 248, 255, 0.35) 50%,
              rgba(230, 240, 250, 0.25) 65%,
              transparent 85%
            ),
            /* Tropical cloud band */
            radial-gradient(ellipse 60% 20% at 50% 40%,
              transparent 0%,
              rgba(255, 255, 255, 0.25) 15%,
              rgba(245, 250, 255, 0.35) 30%,
              rgba(235, 245, 255, 0.3) 50%,
              transparent 75%
            ),
            /* Mid-latitude cloud systems */
            radial-gradient(ellipse 35% 25% at 25% 60%,
              transparent 0%,
              rgba(255, 255, 255, 0.2) 25%,
              rgba(240, 248, 255, 0.3) 45%,
              rgba(230, 240, 250, 0.2) 60%,
              transparent 80%
            ),
            radial-gradient(ellipse 30% 22% at 75% 55%,
              transparent 0%,
              rgba(255, 255, 255, 0.22) 20%,
              rgba(240, 248, 255, 0.32) 40%,
              rgba(230, 240, 250, 0.22) 58%,
              transparent 78%
            ),
            /* Swirling cloud wisps - organic patterns */
            radial-gradient(ellipse 25% 18% at 40% 30%,
              transparent 0%,
              rgba(255, 255, 255, 0.18) 30%,
              rgba(240, 248, 255, 0.25) 50%,
              transparent 70%
            ),
            radial-gradient(ellipse 22% 16% at 65% 25%,
              transparent 0%,
              rgba(255, 255, 255, 0.2) 28%,
              rgba(240, 248, 255, 0.28) 48%,
              transparent 72%
            ),
            radial-gradient(ellipse 20% 15% at 20% 45%,
              transparent 0%,
              rgba(255, 255, 255, 0.15) 35%,
              rgba(240, 248, 255, 0.22) 52%,
              transparent 75%
            ),
            radial-gradient(ellipse 18% 14% at 80% 65%,
              transparent 0%,
              rgba(255, 255, 255, 0.17) 32%,
              rgba(240, 248, 255, 0.24) 50%,
              transparent 73%
            ),
            /* Subtle high-altitude cirrus clouds */
            radial-gradient(ellipse 30% 12% at 55% 20%,
              transparent 0%,
              rgba(255, 255, 255, 0.12) 40%,
              rgba(240, 248, 255, 0.18) 60%,
              transparent 85%
            ),
            radial-gradient(ellipse 28% 10% at 35% 70%,
              transparent 0%,
              rgba(255, 255, 255, 0.1) 45%,
              rgba(240, 248, 255, 0.16) 65%,
              transparent 88%
            ),
            radial-gradient(circle at 35% 25%, 
              rgba(135, 206, 250, 0.5) 0%,
              rgba(70, 130, 180, 0.3) 30%,
              transparent 60%
            )
          `,
          boxShadow: `
            0 0 20px rgba(70, 130, 180, 0.5),
            0 0 30px rgba(34, 139, 34, 0.3),
            0 0 40px rgba(135, 206, 250, 0.2),
            inset -30px -30px 60px rgba(0, 0, 0, 0.9),
            inset 20px 20px 40px rgba(255, 255, 255, 0.15),
            inset -15px 0 30px rgba(0, 0, 0, 0.7)
          `,
          "@media screen and (min-width: 600px)": {
            boxShadow: `
              0 0 40px rgba(70, 130, 180, 0.5),
              0 0 60px rgba(34, 139, 34, 0.3),
              0 0 80px rgba(135, 206, 250, 0.2),
              inset -60px -60px 120px rgba(0, 0, 0, 0.9),
              inset 40px 40px 80px rgba(255, 255, 255, 0.15),
              inset -30px 0 60px rgba(0, 0, 0, 0.7)
            `,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "50%",
            background: `
              radial-gradient(ellipse 50% 100% at 50% 50%,
                transparent 0%,
                rgba(0, 0, 0, 0.3) 40%,
                rgba(0, 0, 0, 0.55) 60%,
                rgba(0, 0, 0, 0.85) 100%
              )
            `,
            mixBlendMode: "multiply",
            zIndex: 2,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "50%",
            background: `
              /* Realistic cloud overlay - soft, wispy, atmospheric */
              /* Major cloud formations with soft edges */
              radial-gradient(ellipse 50% 40% at 48% 48%,
                transparent 0%,
                rgba(255, 255, 255, 0.15) 25%,
                rgba(250, 252, 255, 0.25) 40%,
                rgba(240, 248, 255, 0.2) 55%,
                rgba(230, 240, 250, 0.12) 70%,
                transparent 90%
              ),
              /* Swirling cloud patterns */
              radial-gradient(ellipse 38% 28% at 30% 58%,
                transparent 0%,
                rgba(255, 255, 255, 0.12) 30%,
                rgba(245, 250, 255, 0.22) 48%,
                rgba(235, 245, 255, 0.15) 65%,
                transparent 85%
              ),
              radial-gradient(ellipse 35% 26% at 72% 52%,
                transparent 0%,
                rgba(255, 255, 255, 0.13) 28%,
                rgba(245, 250, 255, 0.23) 46%,
                rgba(235, 245, 255, 0.16) 63%,
                transparent 87%
              ),
              /* Wispy cloud trails */
              radial-gradient(ellipse 25% 15% at 45% 28%,
                transparent 0%,
                rgba(255, 255, 255, 0.1) 35%,
                rgba(240, 248, 255, 0.18) 55%,
                transparent 80%
              ),
              radial-gradient(ellipse 22% 13% at 60% 68%,
                transparent 0%,
                rgba(255, 255, 255, 0.08) 40%,
                rgba(240, 248, 255, 0.15) 58%,
                transparent 82%
              )
            `,
            filter: "blur(2.5px)",
            mixBlendMode: "overlay",
            opacity: 0.85,
            zIndex: 1,
          },
        }}
      />
        </div>
      </Drift>
    </div>

    {/* Jupiter Planet - no ring since Jupiter's rings are extremely faint */}
    <div
      sx={{
        position: "absolute",
        left: ["65%", "72%"],
        top: ["45%", "48%"],
        "@media screen and (max-width: 600px)": {
          left: "60%",
          top: "42%",
        },
      }}
    >
      <Float>
        <div
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: ["75px", "100px"],
            height: ["75px", "100px"],
            borderRadius: "50%",
            overflow: "hidden",
            animation: `${rotatePlanet} 80s linear infinite reverse`,
            willChange: "transform",
            backfaceVisibility: "hidden",
            transition: "transform 0.3s ease-out",
            filter: "drop-shadow(0 0 12px rgba(220, 180, 120, 0.4)) drop-shadow(0 0 20px rgba(220, 20, 60, 0.2))",
            "&:hover": {
              transform: "translate(-50%, -50%) scale(1.08)",
              zIndex: 20,
            },
          }}
        >
      <div
        sx={{
          width: "100%",
          height: "100%",
          background: `
            linear-gradient(0deg,
              rgba(220, 180, 120, 0.8) 0%,
              rgba(255, 218, 185, 0.75) 6%,
              rgba(220, 180, 120, 0.8) 12%,
              rgba(200, 160, 100, 0.75) 18%,
              rgba(220, 180, 120, 0.8) 24%,
              rgba(255, 218, 185, 0.75) 30%,
              rgba(220, 180, 120, 0.8) 36%,
              rgba(200, 160, 100, 0.75) 42%,
              rgba(220, 180, 120, 0.8) 48%,
              rgba(255, 218, 185, 0.75) 54%,
              rgba(220, 180, 120, 0.8) 60%,
              rgba(200, 160, 100, 0.75) 66%,
              rgba(220, 180, 120, 0.8) 72%,
              rgba(255, 218, 185, 0.75) 78%,
              rgba(220, 180, 120, 0.8) 84%,
              rgba(200, 160, 100, 0.75) 90%,
              rgba(220, 180, 120, 0.8) 96%,
              rgba(255, 218, 185, 0.75) 100%
            ),
            radial-gradient(ellipse 25% 35% at 60% 45%,
              rgba(220, 20, 60, 0.6) 0%,
              rgba(178, 34, 34, 0.5) 40%,
              rgba(139, 0, 0, 0.3) 70%,
              transparent 100%
            ),
            radial-gradient(ellipse 60% 40% at 35% 30%,
              rgba(255, 240, 200, 0.5) 0%,
              rgba(255, 218, 185, 0.3) 50%,
              transparent 80%
            )
          `,
          boxShadow: `
            0 0 15px rgba(220, 180, 120, 0.5),
            0 0 25px rgba(200, 160, 100, 0.4),
            0 0 35px rgba(220, 20, 60, 0.2),
            inset -20px -20px 40px rgba(0, 0, 0, 0.8),
            inset 15px 15px 30px rgba(255, 240, 200, 0.2),
            inset -12px 0 25px rgba(0, 0, 0, 0.6)
          `,
          "@media screen and (min-width: 600px)": {
            boxShadow: `
              0 0 30px rgba(220, 180, 120, 0.5),
              0 0 50px rgba(200, 160, 100, 0.4),
              0 0 70px rgba(220, 20, 60, 0.2),
              inset -35px -35px 80px rgba(0, 0, 0, 0.8),
              inset 25px 25px 60px rgba(255, 240, 200, 0.2),
              inset -20px 0 40px rgba(0, 0, 0, 0.6)
            `,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "50%",
            background: `
              radial-gradient(ellipse 45% 100% at 50% 50%,
                transparent 0%,
                rgba(0, 0, 0, 0.3) 40%,
                rgba(0, 0, 0, 0.55) 60%,
                rgba(0, 0, 0, 0.85) 100%
              )
            `,
            mixBlendMode: "multiply",
            zIndex: 2,
          },
        }}
      />
        </div>
      </Float>
    </div>

    {/* Moon */}
    <Twinkle>
      <div
        sx={{
          position: "absolute",
          left: ["45%", "48%"],
          top: ["65%", "68%"],
          width: ["50px", "70px"],
          height: ["50px", "70px"],
          borderRadius: "50%",
          overflow: "hidden",
          background: `
            radial-gradient(circle at 50% 50%, 
              rgba(192, 192, 192, 0.9) 0%,
              rgba(169, 169, 169, 0.8) 25%,
              rgba(128, 128, 128, 0.7) 50%,
              rgba(105, 105, 105, 0.6) 75%,
              rgba(64, 64, 64, 0.5) 100%
            ),
            radial-gradient(ellipse 25% 18% at 60% 50%,
              rgba(0, 0, 0, 0.5) 0%,
              rgba(0, 0, 0, 0.3) 30%,
              transparent 60%
            ),
            radial-gradient(ellipse 18% 15% at 30% 70%,
              rgba(0, 0, 0, 0.4) 0%,
              rgba(0, 0, 0, 0.25) 35%,
              transparent 65%
            ),
            radial-gradient(ellipse 15% 12% at 75% 30%,
              rgba(0, 0, 0, 0.45) 0%,
              rgba(0, 0, 0, 0.3) 40%,
              transparent 70%
            )
          `,
          boxShadow: `
            0 0 15px rgba(192, 192, 192, 0.4),
            0 0 25px rgba(169, 169, 169, 0.3),
            inset -20px -20px 40px rgba(0, 0, 0, 0.7),
            inset 12px 12px 25px rgba(255, 255, 255, 0.15),
            inset -12px 0 25px rgba(0, 0, 0, 0.6)
          `,
          "@media screen and (min-width: 600px)": {
            boxShadow: `
              0 0 25px rgba(192, 192, 192, 0.4),
              0 0 40px rgba(169, 169, 169, 0.3),
              inset -30px -30px 70px rgba(0, 0, 0, 0.7),
              inset 20px 20px 45px rgba(255, 255, 255, 0.15),
              inset -18px 0 35px rgba(0, 0, 0, 0.6)
            `,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "50%",
            background: `
              radial-gradient(ellipse 45% 100% at 50% 50%,
                transparent 0%,
                rgba(0, 0, 0, 0.3) 45%,
                rgba(0, 0, 0, 0.6) 65%,
                rgba(0, 0, 0, 0.85) 100%
              )
            `,
            mixBlendMode: "multiply",
          },
        }}
      />
    </Twinkle>

    {/* Mars Planet */}
    <Drift>
      <div
        sx={{
          position: "absolute",
          left: ["75%", "83%"],
          top: ["10%", "12%"],
          width: ["65px", "90px"],
          height: ["65px", "90px"],
          borderRadius: "50%",
          overflow: "hidden",
          animation: `${rotatePlanet} 100s linear infinite`,
          willChange: "transform",
          backfaceVisibility: "hidden",
          transition: "transform 0.3s ease-out",
          "&:hover": {
            transform: "scale(1.1)",
            zIndex: 20,
          },
          "@media screen and (max-width: 600px)": {
            left: "70%",
            top: "8%",
          },
          background: `
            radial-gradient(ellipse 100% 60% at 50% 50%, 
              rgba(220, 20, 60, 0.8) 0%,
              rgba(178, 34, 34, 0.7) 30%,
              rgba(139, 0, 0, 0.6) 60%,
              rgba(101, 0, 0, 0.5) 100%
            ),
            radial-gradient(ellipse 60% 25% at 50% 10%,
              rgba(240, 248, 255, 0.6) 0%,
              rgba(230, 240, 255, 0.4) 40%,
              transparent 70%
            ),
            radial-gradient(ellipse 60% 25% at 50% 90%,
              rgba(240, 248, 255, 0.6) 0%,
              rgba(230, 240, 255, 0.4) 40%,
              transparent 70%
            )
          `,
          boxShadow: `
            0 0 20px rgba(220, 20, 60, 0.5),
            0 0 30px rgba(178, 34, 34, 0.4),
            0 0 40px rgba(139, 0, 0, 0.2),
            inset -25px -25px 50px rgba(0, 0, 0, 0.8),
            inset 18px 18px 35px rgba(255, 140, 0, 0.2),
            inset -15px 0 30px rgba(0, 0, 0, 0.7)
          `,
          "@media screen and (min-width: 600px)": {
            boxShadow: `
              0 0 30px rgba(220, 20, 60, 0.5),
              0 0 50px rgba(178, 34, 34, 0.4),
              0 0 70px rgba(139, 0, 0, 0.2),
              inset -40px -40px 85px rgba(0, 0, 0, 0.8),
              inset 30px 30px 60px rgba(255, 140, 0, 0.2),
              inset -22px 0 40px rgba(0, 0, 0, 0.7)
            `,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "50%",
            background: `
              radial-gradient(ellipse 45% 100% at 50% 50%,
                transparent 0%,
                rgba(0, 0, 0, 0.25) 40%,
                rgba(0, 0, 0, 0.5) 60%,
                rgba(0, 0, 0, 0.8) 100%
              )
            `,
            mixBlendMode: "multiply",
          },
        }}
      />
    </Drift>

    {/* Neptune Planet */}
    <Float>
      <div
        sx={{
          position: "absolute",
          left: ["18%", "22%"],
          top: ["70%", "73%"],
          width: ["40px", "55px"],
          height: ["40px", "55px"],
          borderRadius: "50%",
          overflow: "hidden",
          animation: `${rotatePlanet} 90s linear infinite reverse`,
          willChange: "transform",
          backfaceVisibility: "hidden",
          transition: "transform 0.3s ease-out",
          "&:hover": {
            transform: "scale(1.15)",
            zIndex: 20,
          },
          "@media screen and (max-width: 600px)": {
            left: "15%",
            top: "68%",
          },
          background: `
            radial-gradient(ellipse 100% 60% at 50% 50%, 
              rgba(0, 191, 255, 0.85) 0%,
              rgba(0, 150, 255, 0.8) 25%,
              rgba(0, 100, 200, 0.75) 50%,
              rgba(0, 50, 150, 0.7) 75%,
              rgba(0, 30, 100, 0.65) 100%
            ),
            linear-gradient(0deg,
              rgba(0, 191, 255, 0.5) 0%,
              rgba(0, 150, 255, 0.4) 8%,
              rgba(0, 100, 200, 0.5) 16%,
              rgba(0, 150, 255, 0.4) 24%,
              rgba(0, 191, 255, 0.5) 32%,
              rgba(0, 100, 200, 0.4) 40%,
              rgba(0, 150, 255, 0.5) 48%,
              rgba(0, 191, 255, 0.4) 56%,
              rgba(0, 100, 200, 0.5) 64%,
              rgba(0, 150, 255, 0.4) 72%,
              rgba(0, 191, 255, 0.5) 80%,
              rgba(0, 100, 200, 0.4) 88%,
              rgba(0, 150, 255, 0.5) 96%,
              rgba(0, 191, 255, 0.4) 100%
            ),
            radial-gradient(ellipse 30% 20% at 65% 50%,
              rgba(0, 30, 100, 0.7) 0%,
              rgba(0, 50, 150, 0.6) 40%,
              rgba(0, 100, 200, 0.4) 70%,
              transparent 100%
            )
          `,
          boxShadow: `
            0 0 20px rgba(0, 191, 255, 0.6),
            0 0 30px rgba(0, 150, 255, 0.5),
            0 0 40px rgba(0, 100, 200, 0.4),
            0 0 50px rgba(0, 191, 255, 0.2),
            inset -15px -15px 35px rgba(0, 0, 0, 0.6),
            inset 10px 10px 20px rgba(135, 206, 250, 0.2),
            inset -10px 0 20px rgba(0, 0, 0, 0.5)
          `,
          "@media screen and (min-width: 600px)": {
            boxShadow: `
              0 0 30px rgba(0, 191, 255, 0.6),
              0 0 50px rgba(0, 150, 255, 0.5),
              0 0 70px rgba(0, 100, 200, 0.4),
              0 0 90px rgba(0, 191, 255, 0.2),
              inset -25px -25px 55px rgba(0, 0, 0, 0.6),
              inset 15px 15px 30px rgba(135, 206, 250, 0.2),
              inset -15px 0 30px rgba(0, 0, 0, 0.5)
            `,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "50%",
            background: `
              radial-gradient(ellipse 45% 100% at 50% 50%,
                transparent 0%,
                rgba(0, 0, 0, 0.2) 40%,
                rgba(0, 0, 0, 0.4) 60%,
                rgba(0, 0, 0, 0.7) 100%
              )
            `,
            mixBlendMode: "multiply",
          },
        }}
      />
    </Float>
  </Divider>
);

export default Planets;

